// src/pages/auth/LoginPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, TextField, Button, Alert, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';

const FIELD_SX = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#0A0A0A',
    borderRadius: '8px',
    fontFamily: 'Inter, sans-serif',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.18)' },
    '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
  },
  '& .MuiInputLabel-root': { color: '#555', fontFamily: 'Inter, sans-serif', '&.Mui-focused': { color: '#fff' } },
  '& .MuiInputBase-input': { color: '#fff', fontFamily: 'Inter, sans-serif', fontSize: '15px' },
};

export default function LoginPage({ onBack }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isLinkSent, setIsLinkSent] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);

  const { sendMagicLink, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) navigate('/dubbing');
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) { setError('Please enter your email address'); return; }
    setLoading(true);
    setError('');
    try {
      const response = await sendMagicLink(email);
      setIsNewUser(response.is_new_user || false);
      setIsLinkSent(true);
    } catch (err) {
      setError(err.message || 'Failed to send magic link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setIsLinkSent(false); setError(''); setIsNewUser(false); setEmail(''); };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#000000', display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2, py: 8 }}>
      <Box sx={{ width: '100%', maxWidth: '420px' }}>

        {/* Logo */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          sx={{ textAlign: 'center', mb: 8 }}
        >
          <Typography
            onClick={() => navigate('/')}
            sx={{ cursor: 'pointer', display: 'inline-block', fontFamily: 'Inter, sans-serif', fontWeight: 800, fontSize: '22px', letterSpacing: '-0.04em' }}
          >
            <span style={{ color: '#FFFFFF' }}>Dub</span>
            <span style={{ color: 'rgba(255,255,255,0.35)' }}>Labs</span>
          </Typography>
        </Box>

        {/* Card */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
          sx={{ p: { xs: 3.5, md: 4.5 }, borderRadius: '16px', backgroundColor: '#0A0A0A', border: '1px solid rgba(255,255,255,0.07)' }}
        >
          {!isLinkSent ? (
            <>
              <Typography sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '22px', fontFamily: 'Inter, sans-serif', letterSpacing: '-0.025em', mb: 0.75 }}>
                Sign in to DubLabs
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '14px', fontFamily: 'Inter, sans-serif', mb: 4 }}>
                Enter your email to receive a magic sign-in link.
              </Typography>

              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  placeholder="you@company.com"
                  sx={{ ...FIELD_SX, mb: 3 }}
                />

                {error && (
                  <Alert severity="error" sx={{ mb: 2, borderRadius: '8px', backgroundColor: 'rgba(239,68,68,0.08)', color: '#F87171', border: '1px solid rgba(239,68,68,0.15)', fontFamily: 'Inter, sans-serif', '& .MuiAlert-icon': { color: '#F87171' } }}>
                    {error}
                  </Alert>
                )}

                <Button
                  type="submit"
                  fullWidth
                  disabled={loading}
                  component={motion.button}
                  whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}
                  startIcon={loading ? <CircularProgress size={16} sx={{ color: '#000' }} /> : null}
                  sx={{ backgroundColor: '#FFFFFF', color: '#000000', fontWeight: 700, fontSize: '15px', py: 1.5, borderRadius: '8px', textTransform: 'none', fontFamily: 'Inter, sans-serif', '&:hover': { backgroundColor: '#F0F0F0' }, '&:disabled': { backgroundColor: 'rgba(255,255,255,0.15)', color: 'rgba(0,0,0,0.4)' } }}
                >
                  {loading ? 'Sending link…' : 'Continue with Email'}
                </Button>
              </Box>

              <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '12px', fontFamily: 'Inter, sans-serif', textAlign: 'center', lineHeight: 1.6 }}>
                  New users will have their account created automatically.
                </Typography>
              </Box>
            </>
          ) : (
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ width: 48, height: 48, borderRadius: '12px', backgroundColor: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
                <Typography sx={{ fontSize: '22px' }}>✓</Typography>
              </Box>
              <Typography sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '20px', fontFamily: 'Inter, sans-serif', mb: 1 }}>
                {isNewUser ? 'Account created!' : 'Check your email'}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '14px', fontFamily: 'Inter, sans-serif', mb: 1, lineHeight: 1.65 }}>
                {isNewUser
                  ? 'We\'ve sent a magic link to complete your registration.'
                  : 'We\'ve sent a sign-in link to your inbox.'}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px', fontFamily: 'Inter, sans-serif', fontWeight: 500, mb: 4 }}>
                {email}
              </Typography>

              <Box sx={{ p: 2.5, borderRadius: '8px', backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', mb: 3 }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '12px', fontFamily: 'Inter, sans-serif', lineHeight: 1.6 }}>
                  Didn't receive it? Check your spam folder. The link expires in 15 minutes.
                </Typography>
              </Box>

              <Button
                onClick={reset}
                sx={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '14px', textTransform: 'none', '&:hover': { color: '#fff', backgroundColor: 'transparent' } }}
              >
                Use a different email
              </Button>
            </Box>
          )}
        </Box>

        {/* Back link */}
        <Box
          component={motion.div}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
          sx={{ textAlign: 'center', mt: 4 }}
        >
          <Button
            onClick={onBack || (() => navigate('/'))}
            startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
            sx={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '13px', textTransform: 'none', '&:hover': { color: 'rgba(255,255,255,0.6)', backgroundColor: 'transparent' } }}
          >
            Back to home
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
